import { event as gtagEvent } from 'vue-gtag';
import { useUserStore } from '@/stores/useUserStore';
import i18n from '@/../lib/plugins/i18n';
import isMobile from 'is-mobile';

// localStorage keys
const LS_KEYS = {
  firstBannerGenerated: 'analytics_first_banner_generated',
  firstDownload: 'analytics_first_download',
  pendingSignupComplete: 'analytics_pending_signup_complete',
  knownUsers: 'analytics_known_user_ids',
  authTrigger: 'analytics_auth_trigger',
};

const deviceType = isMobile() ? 'mobile' : 'desktop';

function getUserType(userStore) {
  if (!userStore.isAuthenticated) return 'anonymous';
  if (userStore.hasFullPremium) return 'premium_business';
  if (userStore.hasPartPremium) return 'premium_freelance';
  if (userStore.hasIndividual) return 'premium_individual';
  return 'free';
}

function getCommonDimensions() {
  try {
    const userStore = useUserStore();
    return {
      is_authenticated: userStore.isAuthenticated,
      user_type: getUserType(userStore),
      locale: i18n.global.locale,
      device_type: deviceType,
    };
  } catch {
    return {
      is_authenticated: false,
      user_type: 'anonymous',
      locale: i18n.global.locale,
      device_type: deviceType,
    };
  }
}

export function trackEvent(eventName, params = {}) {
  const payload = {
    ...getCommonDimensions(),
    ...params,
  };

  gtagEvent(eventName, payload);
}

// --- Page view ---

export function trackPageView(routeName, routePath) {
  trackEvent('custom_page_view', {
    page_title: routeName,
    page_path: routePath,
  });
}

// --- Auth trigger persistence (survives Google OAuth redirect) ---

export function setAuthTrigger(trigger) {
  if (trigger) localStorage.setItem(LS_KEYS.authTrigger, trigger);
}

export function getAndClearAuthTrigger() {
  const trigger = localStorage.getItem(LS_KEYS.authTrigger);
  localStorage.removeItem(LS_KEYS.authTrigger);
  return trigger || 'direct';
}

// --- Auth events ---

export function trackClickSignup(method = 'email', trigger) {
  trackEvent('click_signup', {
    login_method: method,
    auth_trigger: trigger || 'direct',
  });
}

export function trackSignupComplete(method = 'email', trigger) {
  trackEvent('signup_complete', {
    login_method: method,
    auth_trigger: trigger || 'direct',
  });
}

export function trackLoginComplete(method = 'email', trigger) {
  trackEvent('login_complete', {
    login_method: method,
    auth_trigger: trigger || 'direct',
  });
}

/**
 * Sets a pending flag after account creation.
 * signup_complete will fire when SocialModal closes.
 */
export function setPendingSignupComplete(method, trigger) {
  localStorage.setItem(
    LS_KEYS.pendingSignupComplete,
    JSON.stringify({ method, trigger }),
  );
}

/**
 * Called from SocialModal.closeModal().
 * Fires signup_complete if a pending flag exists, then clears it.
 */
export function checkAndFireSignupComplete() {
  const raw = localStorage.getItem(LS_KEYS.pendingSignupComplete);
  if (raw) {
    try {
      const { method, trigger } = JSON.parse(raw);
      trackSignupComplete(method, trigger);
    } catch {
      // Fallback for old string format
      trackSignupComplete(raw);
    }
    localStorage.removeItem(LS_KEYS.pendingSignupComplete);
  }
}

// --- Google auth: known users ---

export function isKnownUser(userId) {
  const known = JSON.parse(localStorage.getItem(LS_KEYS.knownUsers) || '[]');
  return known.includes(userId);
}

export function markKnownUser(userId) {
  const known = JSON.parse(localStorage.getItem(LS_KEYS.knownUsers) || '[]');
  if (!known.includes(userId)) {
    known.push(userId);
    localStorage.setItem(LS_KEYS.knownUsers, JSON.stringify(known));
  }
}

// --- Banner generation events ---

export function trackBannerGenerated(styleId, format) {
  const userStore = useUserStore();
  trackEvent('banner_generated', {
    banner_style: styleId,
    banner_format: format,
    generations_remaining: userStore.user?.limit ?? 0,
  });
}

export function trackFirstBannerGenerated(styleId, format) {
  if (localStorage.getItem(LS_KEYS.firstBannerGenerated)) return;
  localStorage.setItem(LS_KEYS.firstBannerGenerated, Date.now().toString());
  const userStore = useUserStore();
  trackEvent('first_banner_generated', {
    banner_style: styleId,
    banner_format: format,
    generations_remaining: userStore.user?.limit ?? 0,
  });
}

// --- Download events ---

export function trackBannerDownload(style, size) {
  trackEvent('banner_download', {
    banner_style: style,
    banner_size: size,
  });
}

export function trackFirstDownload() {
  if (localStorage.getItem(LS_KEYS.firstDownload)) return;
  localStorage.setItem(LS_KEYS.firstDownload, Date.now().toString());
  trackEvent('first_download');
}

// --- Subscription events ---

export function trackViewPricing() {
  trackEvent('view_pricing');
}

export function trackClickSubscribe(planType) {
  trackEvent('click_subscribe', { plan_type: planType });
}

export function trackCheckoutOpened(planType) {
  trackEvent('checkout_opened', { plan_type: planType });
}

export function trackSubscriptionStarted(planType, priceId) {
  const userStore = useUserStore();
  trackEvent('subscription_started', {
    plan_type: planType,
    price_id: priceId,
    had_previous_plan: !!userStore.hasPremium,
  });
}

// --- Limit ---

export function trackLimitReached() {
  trackEvent('limit_reached', {
    generations_remaining: 0,
  });
}

// --- Cleanup ---

export function resetFirstEventFlags() {
  localStorage.removeItem(LS_KEYS.firstBannerGenerated);
  localStorage.removeItem(LS_KEYS.firstDownload);
  localStorage.removeItem(LS_KEYS.pendingSignupComplete);
  localStorage.removeItem(LS_KEYS.authTrigger);
}
